import {
  createBoundedSerialQueue,
  isDeadlineExceededError,
  withDeadline,
} from "@-/dev.improve.haniverse.lib.deadline";
import type { ActorSyncConnectionLock } from "#actor-sync-connection-lock";
import type {
  ActorTursoSyncConnection,
  ActorTursoSyncStats,
} from "#open-actor-turso-sync-connection-types";

/**
 * How long a pull's CALLER may wait — from the moment it calls, including any time in line behind
 * another sync step — before it is treated as never going to settle.
 *
 * WHY THIS BOUND EXISTS: an unsettled pull produces no error, no rejection and no event, just a
 * spinner that never clears. That is the observed failure when the local dev sync server's
 * `actor-sync.db` is recreated under a still-running app (for example, after an explicit
 * `--reset-sync-data`): the client keeps a cursor for a generation the server no longer has, and
 * the pull never comes back.
 *
 * Both platforms disable long polling. The 30s budget tolerates a slow network while
 * detecting a stalled request without mistaking it for an unchanged revision. It is armed at CALL
 * time, not when the step starts: a caller queued behind a wedged step used to wait for that step
 * first and only then start its own 30s.
 */
const WAIT_CHANGES_TIMEOUT_MS = 30_000;

/**
 * How long a push's caller may wait, including time in line. An upload of every unsent change on a
 * slow link; the engine exposes no upload progress to restart a deadline on, so this is a generous
 * fixed bound rather than a tight one.
 */
export const PUSH_TIMEOUT_MS = 120_000;

/**
 * How long a sync STEP may keep its place in the sync queue after its caller has given up — the
 * "certainly wedged" threshold, past which the queue is poisoned.
 *
 * A step whose caller timed out keeps running on purpose: the native call cannot be cancelled and
 * is still writing sync metadata, so the next step must not start beside it (that keeps a late
 * batch applying safely, and reported by the next pull). But it keeps its place only this long.
 * These sit above everything a healthy step can take — the native per-call deadlines in
 * `open-owned-turso-generation.ts` (`waitChanges` 90s, `push` 180s) plus, for a pull, the apply's
 * wait for and hold of the connection lock — so on native the engine chokepoint reports a stalled
 * call first and this only catches a wedge in the JavaScript around it.
 */
const PULL_STEP_DEADLINE_MS = 300_000;
const PUSH_STEP_DEADLINE_MS = 200_000;

/** How long `close()` waits for a running sync step before abandoning it. */
const CLOSE_WAIT_TIMEOUT_MS = 15_000;

/**
 * How long a `stats()` step may keep its place: a read of the engine's sync metadata, which the
 * native chokepoint already bounds at its default per-call 30s (`open-owned-turso-generation.ts`),
 * so like the other step deadlines this sits above it. Its caller may first wait in line behind a
 * push, which is the point: stats read after that push reflect what it uploaded.
 */
const STATS_STEP_DEADLINE_MS = 45_000;

/**
 * Thrown when the network half of a pull never settles.
 *
 * A DISTINCT ERROR, NOT A `false` RETURN: `pull()` returns `false` for "no changes", which is a
 * healthy, extremely common outcome. Collapsing a timeout into it would restore the exact silence
 * this bound exists to remove — the sync would look idle rather than broken, which is the
 * silent-divergence failure mode this engine already has scar tissue about.
 */
export class ActorSyncPullTimeoutError extends Error {
  override readonly name = "ActorSyncPullTimeoutError";

  constructor(timeoutMs: number, cause?: unknown) {
    super(
      `Actor sync pull did not settle within ${timeoutMs}ms. The sync server stopped answering ` +
        `this client. In local dev this is usually the actor sync DB being recreated under a ` +
        `running app. Preserve local data and recover the previous server database from its ` +
        `actor-sync-backup directory. Normal dev starts preserve sync data.`,
      { cause },
    );
  }
}

/** The caller-side operation name of a pull; its deadline error becomes the named pull error. */
const PULL_OPERATION = "Actor sync pull";

/**
 * Apply {@link WAIT_CHANGES_TIMEOUT_MS} to the network half of a pull, converting its expiry into
 * {@link ActorSyncPullTimeoutError} and passing every other rejection through untouched.
 */
async function withPullTimeout<T>(pending: () => Promise<T>): Promise<T> {
  try {
    return await withDeadline(
      { deadline: WAIT_CHANGES_TIMEOUT_MS, operation: PULL_OPERATION },
      () => pending(),
    );
  } catch (error) {
    if (isDeadlineExceededError(error) && error.operation === PULL_OPERATION) {
      throw new ActorSyncPullTimeoutError(WAIT_CHANGES_TIMEOUT_MS, error);
    }
    throw error;
  }
}

/**
 * Bound a single-phase `pull()` — the web path, which has no `waitChanges`/`applyChanges` split
 * (the two-phase codemod is React-Native-only). Exported so web shares this module's timeout and
 * error type rather than duplicating the bound.
 */
export async function pullWithTimeout(tursoDb: { pull(): Promise<boolean> }): Promise<boolean> {
  return withPullTimeout(() => tursoDb.pull());
}

/**
 * The two-phase shape of a patched `@tursodatabase/sync-react-native` `Database` (see
 * `configs/patching/codemods/tursodatabase__sync-react-native`): `pull()` split into its network
 * wait and its local apply. `TChanges` is the engine's opaque changes handle.
 */
export interface TwoPhaseTursoSyncDatabase<TChanges> {
  applyChanges(changes: TChanges): Promise<void>;
  close(): Promise<void> | void;
  push(): Promise<void>;
  stats(): Promise<ActorTursoSyncStats>;
  waitChanges(): Promise<TChanges | null>;
}

/**
 * Route a Turso sync database's operations through the actor connection lock — holding it ONLY
 * where the engine touches the main connection.
 *
 * - `push()`: outside the application connection lock. Turso 0.7.2's CDC scan and
 *   replay generator use separate untracked reader connections, so its HTTP
 *   upload must not block foreground reads or writes.
 * - `pull()`: `waitChanges()` runs OUTSIDE the lock — per the engine source it is the HTTP
 *   round-trip plus writes to the engine-owned `-changes` file and never touches the main
 *   connection, so app statements run alongside it; `applyChanges()` runs INSIDE the lock — it
 *   opens the engine's write transaction on the main database, and an app `begin` issued mid-apply
 *   fails with "cannot start a transaction within a transaction". `afterApply` runs in the same
 *   exclusive section, so the schema updater sees the downloaded pages before any app statement.
 *
 * A failed apply does NOT poison the connection. One engine keeps syncing after a download it
 * could not apply, with nothing lost (probe `REUSE`), so the failure is only reported and the
 * next cycle tries again. Only a holder that overruns its deadline poisons the lock, as for any
 * other statement.
 * - `close()`: invalidates new work, then waits (bounded) for the running sync step.
 *
 * Holding the lock across the whole `pull()` (the obvious `lock.runExclusive(() => pull())`) put
 * every app read behind the network wait — ~5s stalls on 50ms reads on a staging device.
 *
 * A separate bounded queue serializes push, wait and apply because they share sync metadata.
 * Every caller's wait is bounded from the moment it calls (time in line included). A step whose
 * caller gave up keeps its place until its native work settles — the next step must not start
 * beside it — but only up to its step deadline: past that the sync queue is POISONED (every
 * queued and later sync call fails at once) instead of holding every future sync behind a step
 * that will never finish. The connection's local statements are unaffected by a poisoned sync
 * queue unless the stuck step holds the connection lock (an apply), which the lock's own hold
 * deadline poisons in turn.
 * Source: turso@046e9cbf67d22491e8ecc941ec2891b02a9f3cad,
 * sync/engine/src/database_sync_operations.rs (`push_logical_changes`) and
 * sync/sdk-kit/src/rsapi.rs (`push_changes`, `connect`, `apply_changes`).
 */
export function createLockedActorTursoSync<TChanges>(
  tursoDb: TwoPhaseTursoSyncDatabase<TChanges>,
  lock: ActorSyncConnectionLock,
  {
    afterApply,
    step = (run) => run(),
  }: {
    /** Runs inside the lock right after every applied download, before any app statement. */
    afterApply?: () => Promise<void>;
    /** Wraps each network step, so its failure can be named (see `actor-sync-refusal.ts`). */
    step?: <T>(run: () => Promise<T>) => Promise<T>;
  } = {},
): ActorTursoSyncConnection["tursoDb"] {
  const syncQueue = createBoundedSerialQueue({
    name: "Actor cloud sync",
    onOverrun: "poison",
    runDeadline: PULL_STEP_DEADLINE_MS,
    waitDeadline: WAIT_CHANGES_TIMEOUT_MS,
  });
  let closing: Promise<void> | undefined;
  let unreportedAppliedChanges = false;
  const assertSyncAllowed = () => {
    if (lock.closed) {
      throw new Error("Database connection has closed");
    }
    if (lock.damaged) {
      // A holder of the connection (an earlier apply, or a transaction) overran and may still be
      // inside the engine, so no sync step may ever start beside it.
      const error = new Error(
        "Cloud sync is unavailable on this connection: an earlier database operation never finished",
      );
      syncQueue.poison(error);
      throw error;
    }
    if (lock.recoveryHold) {
      throw new Error(
        "Cloud sync is paused to protect recovered learning data. Choose Save or Load in Cloud Save to resume.",
      );
    }
  };
  const performPull = (): Promise<boolean> => {
    let timedOut = false;
    return withPullTimeout(() =>
      syncQueue
        .run(
          "pull",
          async () => {
            assertSyncAllowed();
            const changes = await step(() => tursoDb.waitChanges());
            if (changes === null) {
              return unreportedAppliedChanges;
            }
            await lock.runExclusive(
              async () => {
                assertSyncAllowed();
                await tursoDb.applyChanges(changes);
                // The pages are in: queries must be refreshed even if what follows fails.
                unreportedAppliedChanges = true;
                await afterApply?.();
              },
              { kind: "sync-apply" },
            );
            return true;
          },
          { runDeadline: PULL_STEP_DEADLINE_MS, waitDeadline: WAIT_CHANGES_TIMEOUT_MS },
        )
        .then((hadChanges) => {
          // A late batch still applies safely. Its timed-out caller cannot
          // invalidate query caches, so the next successful pull reports it.
          if (!timedOut) {
            unreportedAppliedChanges = false;
          }
          return hadChanges;
        }),
    ).catch((error: unknown) => {
      if (error instanceof ActorSyncPullTimeoutError) {
        timedOut = true;
      }
      throw error;
    });
  };
  return {
    close: () => {
      lock.invalidate();
      closing ??= syncQueue
        .run("close", async () => tursoDb.close(), {
          runDeadline: CLOSE_WAIT_TIMEOUT_MS,
          waitDeadline: CLOSE_WAIT_TIMEOUT_MS,
        })
        .catch((error: unknown) => {
          // Still running a step (or poisoned): that step may be inside the engine, so it is never
          // closed under it. Refuse every later step and let the engine's owner abandon it.
          syncQueue.poison(error);
          throw error;
        });
      return closing;
    },
    pull: () => performPull(),
    push: () =>
      withDeadline({ deadline: PUSH_TIMEOUT_MS, operation: "Actor sync push" }, () =>
        syncQueue.run(
          "push",
          async () => {
            assertSyncAllowed();
            return lock.trackBackgroundSync(step(() => tursoDb.push()));
          },
          { runDeadline: PUSH_STEP_DEADLINE_MS, waitDeadline: PUSH_TIMEOUT_MS },
        ),
      ),
    // In line with push, wait and apply: they share the sync metadata this reads. Outside the
    // connection lock like push (the engine reads its own metadata, not the main connection), and
    // allowed during a recovery hold: counting unsent changes uploads nothing.
    stats: () =>
      withDeadline(
        { deadline: PUSH_TIMEOUT_MS + STATS_STEP_DEADLINE_MS, operation: "Actor sync stats" },
        () =>
          syncQueue.run(
            "stats",
            async () => {
              if (lock.closed) {
                throw new Error("Database connection has closed");
              }
              return tursoDb.stats();
            },
            { runDeadline: STATS_STEP_DEADLINE_MS, waitDeadline: PUSH_TIMEOUT_MS },
          ),
      ),
  };
}
